import React, { useState, useEffect, useCallback } from 'react';
import { Tabs, Button, Space, Alert, Typography, Tooltip } from 'antd';
import {
  CodeOutlined,
  FormOutlined,
  ReloadOutlined,
  ClearOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
} from '@ant-design/icons';
import { Input } from 'antd';
import type { AgentType, KrybotTemplate } from '../../../types/krybot';
import { CapabilitiesHelper } from './CapabilitiesHelper';
import { DefaultContextHelper } from './DefaultContextHelper';
import {
  FIELD_DESCRIPTIONS,
  getInputSchemaExample,
  getOutputSchemaExample,
} from '../../../constants/krybotSchemas';

const { TextArea } = Input;
const { Text } = Typography;

export type JsonFieldType = 'capabilities' | 'default_context' | 'input_schema' | 'output_schema';

interface JsonSchemaEditorProps {
  value?: Record<string, unknown> | string;
  onChange?: (value: Record<string, unknown>) => void;
  fieldType: JsonFieldType;
  agentType?: AgentType;
  template?: KrybotTemplate;
  placeholder?: string;
  description?: string;
  disabled?: boolean;
  error?: string;
}

export const JsonSchemaEditor: React.FC<JsonSchemaEditorProps> = ({
  value: propValue,
  onChange: propOnChange,
  fieldType,
  agentType,
  template,
  placeholder,
  description,
  disabled = false,
  error,
}) => {
  // Handle Form.Item integration - value and onChange come from Form.Item
  const value = propValue;
  const onChange = propOnChange || (() => {});
  const [mode, setMode] = useState<'visual' | 'code'>('visual');
  const [jsonString, setJsonString] = useState('');
  const [jsonError, setJsonError] = useState<string | null>(null);
  const [isValid, setIsValid] = useState(true);

  // Parse value - can be object or JSON string
  const parseValue = useCallback((val: Record<string, unknown> | string | undefined): Record<string, unknown> => {
    if (!val) return {};
    if (typeof val === 'string') {
      if (!val.trim()) return {};
      try {
        return JSON.parse(val) || {};
      } catch {
        return {};
      }
    }
    return val || {};
  }, []);

  // Initialize JSON string from value
  useEffect(() => {
    const parsed = parseValue(value);
    try {
      const formatted = JSON.stringify(parsed, null, 2);
      setJsonString(formatted);
      setJsonError(null);
      setIsValid(true);
    } catch (err) {
      setJsonString(String(value));
      setJsonError('Invalid JSON format');
      setIsValid(false);
    }
  }, [value, parseValue]);

  // Handle visual mode changes (from helper components)
  const handleVisualChange = (newValue: Record<string, unknown> | Record<string, boolean>) => {
    onChange(newValue as Record<string, unknown>);
  };

  // Handle code mode changes
  const handleCodeChange = (newJsonString: string) => {
    setJsonString(newJsonString);
    if (!newJsonString.trim()) {
      onChange({});
      setJsonError(null);
      setIsValid(true);
      return;
    }

    try {
      const parsed = JSON.parse(newJsonString);
      onChange(parsed);
      setJsonError(null);
      setIsValid(true);
    } catch (err) {
      setJsonError(err instanceof Error ? err.message : 'Invalid JSON');
      setIsValid(false);
    }
  };

  // Format JSON
  const handleFormat = () => {
    try {
      const parsed = JSON.parse(jsonString);
      const formatted = JSON.stringify(parsed, null, 2);
      setJsonString(formatted);
      handleCodeChange(formatted);
    } catch (err) {
      setJsonError(err instanceof Error ? err.message : 'Invalid JSON');
      setIsValid(false);
    }
  };

  // Clear field
  const handleClear = () => {
    setJsonString('');
    onChange({});
    setJsonError(null);
    setIsValid(true);
  };

  // Load example
  const handleLoadExample = () => {
    let example: Record<string, unknown> = {};
    if (fieldType === 'input_schema') {
      example = getInputSchemaExample(agentType) as Record<string, unknown>;
    } else if (fieldType === 'output_schema') {
      example = getOutputSchemaExample(agentType) as Record<string, unknown>;
    }
    onChange(example);
  };

  // Get field description
  const fieldDesc = FIELD_DESCRIPTIONS[fieldType];

  // Render visual mode based on field type
  const renderVisualMode = () => {
    const parsedValue = parseValue(value);

    switch (fieldType) {
      case 'capabilities': {
        // Cast to Record<string, boolean> for capabilities
        const capabilitiesValue = parsedValue as Record<string, boolean>;
        return (
          <CapabilitiesHelper
            value={capabilitiesValue}
            onChange={handleVisualChange}
            agentType={agentType}
            disabled={disabled}
          />
        );
      }
      case 'default_context': {
        return (
          <DefaultContextHelper
            value={parsedValue}
            onChange={handleVisualChange}
            agentType={agentType}
            disabled={disabled}
          />
        );
      }
      case 'input_schema':
      case 'output_schema': {
        // For schemas, show code editor by default but allow switching
        return renderCodeMode();
      }
      default: {
        return renderCodeMode();
      }
    }
  };

  // Render code mode
  const renderCodeMode = () => {
    return (
      <div className="json-editor-code-mode">
        <div className="json-editor-toolbar">
          <Space>
            <Button
              size="small"
              icon={<ReloadOutlined />}
              onClick={handleFormat}
              disabled={disabled || !jsonString.trim()}
            >
              Format
            </Button>
            {(fieldType === 'input_schema' || fieldType === 'output_schema') && (
              <Button
                size="small"
                onClick={handleLoadExample}
                disabled={disabled}
              >
                Load Example
              </Button>
            )}
            <Button
              size="small"
              icon={<ClearOutlined />}
              onClick={handleClear}
              disabled={disabled}
            >
              Clear
            </Button>
          </Space>
        </div>
        <TextArea
          value={jsonString}
          onChange={(e) => handleCodeChange(e.target.value)}
          placeholder={placeholder || `Enter ${fieldType} as JSON...`}
          disabled={disabled}
          rows={fieldType === 'input_schema' || fieldType === 'output_schema' ? 12 : 8}
          className={`json-editor-textarea ${!isValid ? 'json-editor-error' : ''}`}
        />
        {jsonError && (
          <Alert
            message="JSON Error"
            description={jsonError}
            type="error"
            showIcon
            style={{ marginTop: 8 }}
          />
        )}
        {isValid && jsonString.trim() && (
          <Alert
            message="Valid JSON"
            type="success"
            icon={<CheckCircleOutlined />}
            style={{ marginTop: 8 }}
          />
        )}
      </div>
    );
  };

  // Determine if visual mode is available for this field type
  const hasVisualMode = fieldType === 'capabilities' || fieldType === 'default_context';

  return (
    <div className="json-schema-editor">
      {fieldDesc && (
        <div className="json-editor-description">
          <Text type="secondary">{fieldDesc.description}</Text>
          {fieldDesc.helpText && (
            <div style={{ marginTop: 4 }}>
              <Text type="secondary" style={{ fontSize: '12px' }}>
                {fieldDesc.helpText}
              </Text>
            </div>
          )}
        </div>
      )}

      {hasVisualMode ? (
        <Tabs
          activeKey={mode}
          onChange={(key) => setMode(key as 'visual' | 'code')}
          items={[
            {
              key: 'visual',
              label: (
                <span>
                  <FormOutlined /> Visual
                </span>
              ),
              children: renderVisualMode(),
            },
            {
              key: 'code',
              label: (
                <span>
                  <CodeOutlined /> Code
                </span>
              ),
              children: renderCodeMode(),
            },
          ]}
        />
      ) : (
        renderCodeMode()
      )}

      {error && (
        <Alert
          message={error}
          type="error"
          showIcon
          style={{ marginTop: 8 }}
        />
      )}

      {template && fieldType === 'input_schema' && template.metadata && (
        <Alert
          message={`Template "${template.template_key}" provides schema metadata that can be used for input/output schemas.`}
          type="info"
          style={{ marginTop: 8 }}
        />
      )}
    </div>
  );
};

